/**
 * Foreground Messaging Hook
 *
 * Handles FCM push notifications when the app is in the foreground.
 *
 * Behavior:
 * - Always shows in-app toast (via notificationStore) on all platforms
 * - On mobile: ALSO shows system notification (phone may be in pocket)
 * - On desktop: Relies on in-app toast only (user is watching screen)
 *
 * Why both on mobile?
 * Yard crew often have phones in pockets while working. They need both:
 * - System notification: vibrates/sounds to get attention
 * - In-app toast: visible when they open the app
 *
 * Note: Background notifications are handled by firebase-messaging-sw.js
 * This hook only handles messages received while the app is open.
 */

import { useEffect, useRef } from 'react';
import { onMessage } from 'firebase/messaging';
import { useFirebaseStore } from '../store/firebaseStore';
import { useNotificationStore } from '../store/notificationStore';
import { logger } from '../utils/logger';

// Detect mobile devices via user agent
const isMobileDevice = (): boolean => {
  if (typeof navigator === 'undefined') return false;
  return /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
};

export const useForegroundMessaging = (): void => {
  const { messaging } = useFirebaseStore();
  const addToast = useNotificationStore((state) => state.addToast);
  const hasSetupListener = useRef(false);

  useEffect(() => {
    // Only set up once, and only if messaging is available
    if (!messaging || hasSetupListener.current) return;
    hasSetupListener.current = true;

    logger.log('[ForegroundMessaging] Setting up onMessage listener');

    // Listen for messages when app is in foreground
    const unsubscribe = onMessage(messaging, (payload) => {
      logger.log('[ForegroundMessaging] Received message:', payload);

      const notificationId = payload.data?.id || payload.messageId || crypto.randomUUID();
      const type = payload.data?.type || 'info';
      const title = payload.notification?.title || payload.data?.title || 'New Notification';
      const body = payload.notification?.body || payload.data?.body || '';
      const locationId = payload.data?.locationId || '';

      // Extract metadata from data payload
      const metadata: Record<string, string> = {};
      if (payload.data) {
        for (const [key, value] of Object.entries(payload.data)) {
          // Skip known non-metadata fields
          if (!['id', 'type', 'title', 'body', 'locationId', 'environment'].includes(key)) {
            metadata[key] = value;
          }
        }
      }

      // 1. Always show in-app toast (both platforms)
      addToast({
        id: notificationId,
        type,
        title,
        body,
        locationId,
        metadata: Object.keys(metadata).length > 0 ? metadata : undefined,
        actionBy: payload.data?.actionBy,
        actionByName: payload.data?.actionByName,
      });

      // 2. Mobile: ALSO show system notification (yard crew with phone in pocket)
      // Desktop: Skip - in-app toast is sufficient since user is watching screen
      if (isMobileDevice() && typeof Notification !== 'undefined' && Notification.permission === 'granted') {
        try {
          // eslint-disable-next-line no-new
          new Notification(title, {
            body,
            icon: '/icons/icon-192.png',
            tag: notificationId, // Prevents duplicate system notifications with same ID
            // Note: vibrate is not widely supported but harmless to include
          });
          logger.log('[ForegroundMessaging] System notification shown on mobile');
        } catch (err) {
          // Ignore errors - system notifications are a nice-to-have
          logger.warn('[ForegroundMessaging] Failed to show system notification:', err);
        }
      }
    });

    // Cleanup on unmount (though this should never unmount in AuthenticatedLayout)
    return () => {
      logger.log('[ForegroundMessaging] Cleaning up onMessage listener');
      unsubscribe();
      hasSetupListener.current = false;
    };
  }, [messaging, addToast]);
};
